import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Loader2, RefreshCw, Trash2, UploadCloud } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  type QueuedSale,
  discardSale,
  flushQueue,
  listQueuedSales,
  retryFailedSale,
} from '@/lib/offline-queue'
import { useOfflineSalesSync } from '@/hooks/use-offline-sales-sync'
import { subscribeConnectivity } from '@/lib/connectivity'
import { isStorageAtRisk } from '@/lib/storage-policy'

function formatTime(timestamp: number): string {
  try {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return 'earlier'
  }
}

/**
 * "2 sales and 1 payment", so the count never calls a repayment a sale.
 * Returns an empty string when there is nothing of either kind.
 */
function describeCount(sales: number, payments: number): string {
  const parts: string[] = []
  if (sales > 0) parts.push(`${sales} sale${sales === 1 ? '' : 's'}`)
  if (payments > 0) parts.push(`${payments} payment${payments === 1 ? '' : 's'}`)
  return parts.join(' and ')
}

/**
 * Tells the shop what has not reached the server yet.
 *
 * Queued sales are real revenue: ones still waiting are replayed automatically,
 * and ones the server rejected are shown with their reason so a person can fix
 * the cause (usually stock) and retry — never silently dropped.
 */
export function OfflineSalesStatus() {
  const status = useOfflineSalesSync()
  const [expanded, setExpanded] = useState(false)
  const [failed, setFailed] = useState<QueuedSale[]>([])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [online, setOnline] = useState(true)
  const [storageAtRisk, setStorageAtRisk] = useState(false)

  useEffect(() => subscribeConnectivity((state) => setOnline(state.online)), [])

  // Only matters while something is actually waiting: that is the data the
  // browser could evict.
  useEffect(() => {
    if (status.pending === 0) {
      setStorageAtRisk(false)
      return
    }
    let alive = true
    void isStorageAtRisk().then((risk) => {
      if (alive) setStorageAtRisk(risk)
    })
    return () => {
      alive = false
    }
  }, [status.pending])

  const loadFailed = useCallback(async () => {
    const rows = await listQueuedSales()
    setFailed(rows.filter((sale) => sale.lastError))
  }, [])

  useEffect(() => {
    if (status.failed > 0) void loadFailed()
    else setFailed([])
  }, [status.failed, loadFailed])

  const handleRetry = useCallback(
    async (id: string) => {
      setBusyId(id)
      try {
        await retryFailedSale(id)
        await loadFailed()
      } finally {
        setBusyId(null)
      }
    },
    [loadFailed],
  )

  const handleDiscard = useCallback(
    async (id: string) => {
      setBusyId(id)
      try {
        await discardSale(id)
        await loadFailed()
      } finally {
        setBusyId(null)
      }
    },
    [loadFailed],
  )

  if (status.pending === 0 && status.failed === 0) return null

  // Rejected sales need attention, so they outrank the "waiting" message.
  if (status.failed > 0) {
    return (
      <div
        role="alert"
        data-testid="offline-sales-failed"
        className="border-b border-red-300 bg-red-50 px-4 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100"
      >
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <strong>
              {describeCount(status.failedSales, status.failedPayments) || status.failed} could not
              be synced.
            </strong>
          </span>
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="rounded-md border border-red-400 px-2 py-0.5 font-medium hover:bg-red-100 dark:border-red-700 dark:hover:bg-red-900"
            aria-expanded={expanded}
          >
            {expanded ? 'Hide details' : 'Review'}
          </button>
        </div>

        {expanded && (
          <ul className="mx-auto mt-2 max-w-3xl space-y-2">
            {failed.map((sale) => (
              <li
                key={sale.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-200 bg-white/70 px-3 py-2 dark:border-red-800 dark:bg-red-900/30"
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {sale.kind === 'credit_payment' ? 'Payment' : 'Sale'} from{' '}
                    {formatTime(sale.capturedAt ?? sale.createdAt)}
                    {/* A repayment has no sales slip; only sales carry a receipt. */}
                    {sale.receipt && sale.kind !== 'credit_payment' && (
                      <span className="ml-2 font-mono text-xs opacity-75">{sale.receipt}</span>
                    )}
                  </p>
                  <p className="text-xs leading-relaxed opacity-80">{sale.lastError}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void handleRetry(sale.id)}
                    disabled={busyId === sale.id}
                  >
                    <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                    Retry
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void handleDiscard(sale.id)}
                    disabled={busyId === sale.id}
                    aria-label={`Discard the sale from ${formatTime(sale.createdAt)}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-sales-pending"
      className="border-b border-blue-300 bg-blue-50 px-4 py-2 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
        {status.replaying ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
        ) : (
          <UploadCloud className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <span>
          <strong>
            {describeCount(status.pendingSales, status.pendingPayments) || status.pending} waiting
            to sync.
          </strong>{' '}
          {status.needsSignIn
            ? 'Sign in again to send them — they are safe on this device.'
            : status.replaying
              ? 'Sending now…'
              : 'They will be sent automatically when the connection returns.'}
        </span>
        {status.held > 0 && (
          // Not a failure: the records belong to another company and this
          // session must not post them. The owning account can still send them.
          <span className="rounded-md bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-950">
            {status.held} rung up under another company — sign in as that company to send{" "}
            {status.held === 1 ? "it" : "them"}
          </span>
        )}
        {storageAtRisk && !status.replaying && (
          // The sales are safe for now, but the browser is close to evicting the
          // storage that holds them. Saying so is the only warning anyone gets.
          <span className="rounded-md bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-950">
            Low storage — keep this app open until these sales send
          </span>
        )}
        {status.needsSignIn && (
          // Waiting cannot clear a 401; the session has to be renewed.
          <a
            href="/login"
            className="rounded-md border border-blue-400 px-2 py-0.5 font-medium hover:bg-blue-100 dark:border-blue-700 dark:hover:bg-blue-900"
          >
            Sign in
          </a>
        )}
        {!status.replaying && !status.needsSignIn && (
          // Pointless while offline: it would fail instantly for no reason. The
          // banner above already explains why sending cannot work yet.
          <button
            type="button"
            onClick={() => void flushQueue()}
            disabled={!online}
            title={online ? undefined : 'Once the connection returns'}
            className="rounded-md border border-blue-400 px-2 py-0.5 font-medium hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-700 dark:hover:bg-blue-900"
          >
            Send now
          </button>
        )}
      </div>
    </div>
  )
}

export default OfflineSalesStatus
